import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Download,
  FileCheck,
  KeyRound,
  Lock,
  ShieldCheck,
  Unlock,
  UploadCloud,
  Zap,
} from "lucide-react";
import {
  collect,
  createDecryptStream,
  createEncryptStream,
  generatePasswordSalt,
  parseHeader,
  wrapMasterKey,
} from "@limeshare/lsc1";
import { argon2idKdf } from "../../engine/kdf";
import { chooseSink } from "../../engine/sinks";
import { formatBytes, safeFileName } from "../../util";

export function EncryptFile() {
  const [mode, setMode] = useState<"encrypt" | "decrypt">("encrypt");
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setPassword("");
    setBusy(false);
    setProgressPct(0);
    setCompleted(false);
    setError(null);
  };

  const handleProcess = async () => {
    if (!file) return;
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError(null);
    setBusy(true);
    setProgressPct(0);
    setCompleted(false);

    try {
      if (mode === "encrypt") {
        const salt = generatePasswordSalt();
        const kek = await argon2idKdf(password, salt);

        const outName = `${safeFileName(file.name)}.lsc`;
        const sink = await chooseSink(outName, file.size + 1024);

        const enc = createEncryptStream({
          key: kek,
          plaintextLength: file.size,
          onProgress: (done) => {
            setProgressPct(Math.min(100, Math.round((done / file.size) * 100)));
          },
        });

        // Prepend salt (16 bytes) so decrypter can derive the same KEK
        const { readable, writable } = new TransformStream<Uint8Array, Uint8Array>();
        const writer = writable.getWriter();

        const encryptPromise = (async () => {
          await writer.write(salt);
          const reader = file.stream().pipeThrough(enc.stream).getReader();
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            await writer.write(value);
          }
          await writer.close();
        })();

        await Promise.all([sink.write(readable), encryptPromise]);
        setCompleted(true);
      } else {
        // Decrypt .lsc file
        // Read first 16 bytes for salt
        const saltBlob = file.slice(0, 16);
        const salt = new Uint8Array(await saltBlob.arrayBuffer());
        if (salt.length !== 16) throw new Error("Invalid .lsc container file (too small)");

        const kek = await argon2idKdf(password, salt);

        const outName = file.name.endsWith(".lsc") ? file.name.slice(0, -4) : `decrypted_${file.name}`;
        const sink = await chooseSink(outName, file.size);

        const dec = createDecryptStream({
          key: kek,
          onProgress: (done) => {
            setProgressPct(Math.min(100, Math.round((done / Math.max(1, file.size - 48)) * 100)));
          },
        });

        // Stream from byte 16 onwards (container header + frames)
        const containerStream = file.slice(16).stream().pipeThrough(dec);
        await sink.write(containerStream);
        setCompleted(true);
      }
    } catch (e) {
      setError((e as Error).message || "Operation failed. Check your password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="panel legal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(0, 255, 102, 0.1)", borderRadius: 999, border: "1px solid rgba(0, 255, 102, 0.25)", fontSize: 12, color: "#7cff5c", fontFamily: "ui-monospace, monospace", marginBottom: 12 }}>
          <Lock size={12} /> 100% Client-Side Tool
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          Offline File Encryptor (AES-256-GCM + Argon2id)
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Encrypt or decrypt any file locally on your device without transmitting a single byte over the internet.
          Uses military-grade AES-256-GCM authenticated streaming and Argon2id password key derivation.
        </p>
      </header>

      {/* Mode Switcher */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <button
          className={`btn ${mode === "encrypt" ? "" : "ghost"}`}
          style={{ flex: 1, padding: "10px 16px" }}
          onClick={() => {
            setMode("encrypt");
            reset();
          }}
        >
          <Lock size={15} /> Encrypt to .lsc
        </button>
        <button
          className={`btn ${mode === "decrypt" ? "" : "ghost"}`}
          style={{ flex: 1, padding: "10px 16px" }}
          onClick={() => {
            setMode("decrypt");
            reset();
          }}
        >
          <Unlock size={15} /> Decrypt .lsc file
        </button>
      </div>

      {/* Dropzone */}
      <div
        className="dropzone"
        onClick={() => inputRef.current?.click()}
        style={{ marginBottom: 20 }}
      >
        <div className="dropzone-icon">
          {mode === "encrypt" ? <Lock size={28} /> : <Unlock size={28} />}
        </div>
        <strong>
          {file ? file.name : mode === "encrypt" ? "Choose file to encrypt" : "Choose .lsc file to decrypt"}
        </strong>
        <small>{file ? formatBytes(file.size) : "Processed entirely in browser memory (RAM)"}</small>
        <input
          ref={inputRef}
          type="file"
          hidden
          onChange={(e) => {
            if (e.target.files?.[0]) {
              setFile(e.target.files[0]);
              setError(null);
            }
            e.target.value = "";
          }}
        />
      </div>

      {file && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, color: "var(--text)", display: "block", marginBottom: 6 }}>
            {mode === "encrypt" ? "Create encryption password:" : "Enter decryption password:"}
          </label>
          <input
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters…"
            value={password}
            disabled={busy}
            onChange={(e) => setPassword(e.target.value)}
            className="textinput"
            style={{ marginTop: 0 }}
          />
        </div>
      )}

      {error && <div className="alert error" style={{ marginBottom: 18 }}>{error}</div>}

      {busy && (
        <div className="progress" style={{ marginBottom: 20 }}>
          <div className="bar">
            <div style={{ width: `${progressPct}%` }} />
          </div>
          <div className="meta">
            <span>{mode === "encrypt" ? "Streaming AES-256 encryption…" : "Decrypting & verifying tags…"}</span>
            <span>{progressPct}%</span>
          </div>
        </div>
      )}

      {completed && (
        <div className="alert ok" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <CheckCircle2 size={18} color="#00ff66" />
          <span>
            <b>Success!</b> File {mode === "encrypt" ? "encrypted and saved as .lsc" : "decrypted successfully"}.
          </span>
        </div>
      )}

      {file && !busy && (
        <div className="row" style={{ marginTop: 0 }}>
          <button className="btn block" onClick={handleProcess}>
            {mode === "encrypt" ? (
              <>
                <Lock size={16} /> Encrypt &amp; Save .lsc
              </>
            ) : (
              <>
                <Unlock size={16} /> Decrypt &amp; Save File
              </>
            )}
          </button>
        </div>
      )}

      <section style={{ marginTop: 32 }}>
        <h2>How offline browser encryption works</h2>
        <p>
          Unlike cloud encryptors that upload your file to a remote server, LimeShare executes WebCrypto inside your browser.
          Your password is strengthened with <b>Argon2id</b> (using 64 MB of RAM and 3 cryptographic passes) to derive a 256-bit
          AES key. Each 4 MB chunk is signed with a Galois/Counter Mode authentication tag. Even if an attacker intercepts the
          `.lsc` file, cracking it is computationally infeasible without the password.
        </p>
      </section>

      <div style={{ padding: 22, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", textAlign: "center", marginTop: 28 }}>
        <h3 style={{ margin: "0 0 8px" }}>Need to send this file to someone?</h3>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          Use LimeShare transfer links to encrypt and upload directly to secure cloud storage with zero accounts.
        </p>
        <Link to="/" className="btn" style={{ textDecoration: "none" }}>
          <UploadCloud size={16} /> Share File Online
        </Link>
      </div>
    </article>
  );
}
